/**
 * FERP Portal - Require Architect Access Guard
 *
 * **SECURITY**: Protects architect-only routes from unauthorized access.
 *
 * **Two-layer protection**:
 * 1. Role check: User must have 'architect' or 'superadmin' role
 * 2. Tenant check: User must be from FERP's owning company (not customer tenants)
 *
 * **Why this matters**:
 * Architect routes expose internal system details that customer tenant admins
 * should NEVER see:
 * - Error stack traces (reveal code paths)
 * - Service names (expose architecture)
 * - GraphQL operation internals (show business logic)
 * - System metrics (Prometheus data)
 *
 * **Usage**:
 * ```tsx
 * <Route
 *   path="/architect/federation"
 *   element={
 *     <RequireArchitect>
 *       <SubgraphConstellation />
 *     </RequireArchitect>
 *   }
 * />
 * ```
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { Alert, Stack, Text, Button, Center, Paper } from '@mantine/core';
import { IconShieldLock, IconAlertTriangle } from '@tabler/icons-react';
import { useAuth } from '@/auth/AuthContext';
import { isDevModeEnabled } from '@/utils/env';

/**
 * FERP owner company tenant IDs
 *
 * These are the ONLY tenants whose architects can access architect routes.
 * Customer tenant architects are blocked even if they have the role.
 *
 * **Configuration**:
 * - Add your company's production tenant ID here
 * - 'ferp-system' is for development/testing
 * - In dev mode, tenant check is skipped (DevImpersonationPanel handles switching)
 */
const FERP_OWNER_TENANT_IDS = [
  'ferp-system',           // Development/testing
  // Add production owner tenant IDs here:
  // 'ferp-production-001',
  // 'ferp-ops',
] as const;

interface RequireArchitectProps {
  children: React.ReactNode;
  /**
   * If true, only checks role (allows any tenant's architects).
   * If false (default), checks both role AND tenant ownership.
   *
   * **SECURITY WARNING**: Setting this to true allows customer tenant
   * architects to access internal system details!
   */
  skipTenantCheck?: boolean;
}

export function RequireArchitect({
  children,
  skipTenantCheck = false,
}: RequireArchitectProps): React.ReactElement {
  const { user, tenant, viewLayer, isAuthenticated, isLoading } = useAuth();

  // Still loading - show nothing
  if (isLoading) {
    return <></>;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check 1: Must have architect view layer (role check)
  const hasArchitectRole = viewLayer === 'architect';

  // Check 2: Must be from FERP owner company (tenant check)
  // In dev mode, skip tenant ownership check — DevImpersonationPanel handles tenant switching
  const isOwnerTenant = isDevModeEnabled() || FERP_OWNER_TENANT_IDS.includes(
    tenant?.id as typeof FERP_OWNER_TENANT_IDS[number]
  );

  // PASS: Has architect role AND (is owner tenant OR skip check is enabled)
  if (hasArchitectRole && (isOwnerTenant || skipTenantCheck)) {
    return <>{children}</>;
  }

  // FAIL: Show access denied screen with details
  return (
    <Center h="100vh" p="xl">
      <Paper shadow="md" p="xl" radius="md" maw={600} w="100%">
        <Stack gap="lg">
          <Alert
            color="red"
            title="Access Denied - Architect Privileges Required"
            icon={<IconShieldLock size={24} />}
          >
            <Stack gap="md">
              <Text size="sm">
                This page is restricted to FERP architects from the owning company only.
              </Text>

              {!hasArchitectRole && (
                <Alert color="orange" title="Missing Role" icon={<IconAlertTriangle />}>
                  <Text size="sm">
                    <strong>Required role:</strong> architect or superadmin
                  </Text>
                  <Text size="sm" mt="xs">
                    <strong>Your roles:</strong> {user?.roles?.join(', ') || 'none'}
                  </Text>
                  <Text size="sm" mt="xs" c="dimmed">
                    Contact your system administrator to request architect access.
                  </Text>
                </Alert>
              )}

              {hasArchitectRole && !isOwnerTenant && !skipTenantCheck && (
                <Alert color="orange" title="Tenant Restriction" icon={<IconAlertTriangle />}>
                  <Text size="sm">
                    <strong>Tenant:</strong> {tenant?.name || 'Unknown'} ({tenant?.id})
                  </Text>
                  <Text size="sm" mt="xs" c="dimmed">
                    This page shows internal system details (error stack traces, service
                    architecture, metrics) that are restricted to FERP's owning company.
                  </Text>
                  <Text size="sm" mt="xs" c="dimmed">
                    <strong>Reason</strong>: Customer tenant architects should not have
                    visibility into FERP's internal implementation details.
                  </Text>
                </Alert>
              )}

              <Stack gap="xs" mt="md">
                <Text size="xs" fw={600} c="dimmed">
                  ACCESS REQUIREMENTS:
                </Text>
                <Text size="xs" c="dimmed">
                  ✓ Role: architect or superadmin {hasArchitectRole ? '✅' : '❌'}
                </Text>
                {!skipTenantCheck && (
                  <Text size="xs" c="dimmed">
                    ✓ Tenant: FERP owning company {isOwnerTenant ? '✅' : '❌'}
                  </Text>
                )}
              </Stack>

              <Button
                variant="light"
                fullWidth
                onClick={() => window.history.back()}
                mt="md"
              >
                Go Back
              </Button>
            </Stack>
          </Alert>
        </Stack>
      </Paper>
    </Center>
  );
}

/**
 * Hook to check if current user has architect access
 *
 * **Usage**:
 * ```tsx
 * const { hasAccess, reason } = useArchitectAccess();
 *
 * if (!hasAccess) {
 *   return <Alert>{reason}</Alert>;
 * }
 * ```
 */
export function useArchitectAccess(skipTenantCheck = false) {
  const { user, tenant, viewLayer } = useAuth();

  const hasArchitectRole = viewLayer === 'architect';
  const isOwnerTenant = isDevModeEnabled() || FERP_OWNER_TENANT_IDS.includes(
    tenant?.id as typeof FERP_OWNER_TENANT_IDS[number]
  );

  const hasAccess = hasArchitectRole && (isOwnerTenant || skipTenantCheck);

  let reason = '';
  if (!hasArchitectRole) {
    reason = 'Missing architect role';
  } else if (!isOwnerTenant && !skipTenantCheck) {
    reason = 'Not an owner tenant';
  }

  return {
    hasAccess,
    hasArchitectRole,
    isOwnerTenant,
    reason,
    currentTenant: tenant?.id,
    currentRoles: user?.roles || [],
  };
}

export default RequireArchitect;
